/**
 * WhatsApp Service for MEV-Med Frontend
 * API client for WhatsApp integration endpoints
 */

import apiClient from './httpClient';
import type {
  WhatsAppInstance,
  WhatsAppQRResponse,
  WhatsAppMessageItem,
} from '../types/whatsapp';

class WhatsAppService {
  private baseUrl = '/whatsapp';

  // ====================================================================
  // Instances
  // ====================================================================

  async getInstances(): Promise<WhatsAppInstance[]> {
    const response = await apiClient.get(`${this.baseUrl}/instances`);
    return response.data ?? [];
  }

  async getInstance(id: number): Promise<WhatsAppInstance> {
    const response = await apiClient.get(`${this.baseUrl}/instances/${id}`);
    return response.data;
  }

  async createInstance(data: {
    entityType?: 'provider' | 'company';
    entityId?: number;
    displayName: string;
    isGlobal?: boolean;
  }): Promise<WhatsAppInstance> {
    const response = await apiClient.post(`${this.baseUrl}/instances`, data);
    return response.data;
  }

  async deleteInstance(id: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/instances/${id}`);
  }

  // ====================================================================
  // Connection
  // ====================================================================

  async connectInstance(id: number): Promise<void> {
    await apiClient.post(`${this.baseUrl}/instances/${id}/connect`);
  }

  async disconnectInstance(id: number): Promise<void> {
    await apiClient.post(`${this.baseUrl}/instances/${id}/disconnect`);
  }

  async getQR(id: number): Promise<WhatsAppQRResponse> {
    const response = await apiClient.get<WhatsAppQRResponse>(
      `${this.baseUrl}/instances/${id}/qr`
    );
    return response as unknown as WhatsAppQRResponse;
  }

  // ====================================================================
  // Messages
  // ====================================================================

  async getMessages(
    instanceId: number,
    filters?: { status?: string; page?: number; limit?: number }
  ): Promise<{ data: WhatsAppMessageItem[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const url = `${this.baseUrl}/instances/${instanceId}/messages${params.toString() ? `?${params}` : ''}`;
    const response = await apiClient.get<{ data: WhatsAppMessageItem[]; total: number }>(url);
    return response as unknown as { data: WhatsAppMessageItem[]; total: number };
  }

  async retryMessage(instanceId: number, msgId: number): Promise<void> {
    await apiClient.post(`${this.baseUrl}/instances/${instanceId}/messages/${msgId}/retry`);
  }

  async cancelMessage(instanceId: number, msgId: number): Promise<void> {
    await apiClient.post(`${this.baseUrl}/instances/${instanceId}/messages/${msgId}/cancel`);
  }

  async sendTestMessage(id: number, phone: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/instances/${id}/send-test`, { phone });
  }
}

export const whatsappService = new WhatsAppService();
export default whatsappService;
